import {
  lazy,
  Suspense,
  useEffect,
  useMemo,
  useState,
} from "react";


import exchangeLogo from "./assets/exalt-exchange-logo.png";
import { useI18n } from "./i18n/index.js";
import LanguageSwitcher from "./components/LanguageSwitcher";
import "./style.css";
import NotificationBell from "./components/NotificationBell";
import VerifyEmail from "./components/VerifyEmail";
import ResetPassword from "./components/ResetPassword";
import ForgotPassword from "./components/ForgotPassword";
import VerifyResetCode from "./components/VerifyResetCode";
import AuthPanel from "./components/AuthPanel";
import { AppShell } from "./design-system/AppShell.jsx";
import { useTheme } from "./design-system/ThemeProvider.jsx";
import { Button } from "./design-system/Button.jsx";
import { buildShellNavItems } from "./navigation/buildShellNavItems.js";

const Dashboard = lazy(() =>
  import("./components/Dashboard").then((module) => ({
    default: module.default || module.Dashboard,
  }))
);

const Assets = lazy(() =>
  import("./components/Assets").then((module) => ({
    default: module.default || module.Assets,
  }))
);

const TradFi = lazy(() =>
  import("./components/TradFi").then((module) => ({
    default: module.default || module.TradFi,
  }))
);

const ServicesHub = lazy(() =>
  import("./components/ServicesHub").then((module) => ({
    default: module.default || module.ServicesHub,
  }))
);

const ExaltCard = lazy(() =>
  import("./components/ExaltCard").then((module) => ({
    default: module.default || module.ExaltCard,
  }))
);

const Markets = lazy(() =>
  import("./components/Markets").then((module) => ({
    default: module.default || module.Markets,
  }))
);

const Trade = lazy(() =>
  import("./components/Trade").then((module) => ({
    default: module.default || module.Trade,
  }))
);

const Futures = lazy(() =>
  import("./components/Futures").then((module) => ({
    default: module.default || module.Futures,
  }))
);
const Wallets = lazy(() =>
  import("./components/Wallets").then((module) => ({
    default: module.default || module.Wallets,
  }))
);

const Web3Wallet = lazy(() =>
  import("./components/Web3Wallet").then((module) => ({
    default: module.default || module.Web3Wallet,
  }))
);

const Transactions = lazy(() =>
  import("./components/Transactions").then((module) => ({
    default: module.default || module.Transactions,
  }))
);

const Orders = lazy(() =>
  import("./components/Orders").then((module) => ({
    default: module.default || module.Orders,
  }))
);
const BuyCrypto = lazy(() =>
  import("./components/BuyCrypto").then((module) => ({
    default: module.default || module.BuyCrypto,
  }))
);

const P2P = lazy(() =>
  import("./components/P2P").then((module) => ({
    default: module.default || module.P2P,
  }))
);

const Profile = lazy(() =>
  import("./components/Profile").then((module) => ({
    default: module.default || module.Profile,
  }))
);

const Settings = lazy(() =>
  import("./components/Settings").then((module) => ({
    default: module.default || module.Settings,
  }))
);

const Support = lazy(() =>
  import("./components/Support").then((module) => ({
    default: module.default || module.Support,
  }))
);

const Referral = lazy(() =>
  import("./components/Referral").then((module) => ({
    default: module.default || module.Referral,
  }))
);
const ListingForm = lazy(() =>
  import("./components/ListingForm").then((module) => ({
    default: module.default || module.ListingForm,
  }))
);

const TradingPanel = lazy(() =>
  import("./components/TradingPanel").then((module) => ({
    default: module.default || module.TradingPanel,
  }))
);

const OrderBook = lazy(() =>
  import("./components/OrderBook").then((module) => ({
    default: module.default || module.OrderBook,
  }))
);

const KycVerification = lazy(() =>
  import("./components/kycVerification").then((module) => ({
    default:
      module.default || module.KycVerification,
  }))
);

const ReplitRewards = lazy(() =>
  import("./replit_ui/Rewards").then((module) => ({
    default: module.default || module.ReplitRewards,
  }))
);

const Staking = lazy(() =>
  import("./components/Staking").then((module) => ({
    default: module.default || module.Staking,
  }))
);

const LearnEarn = lazy(() =>
  import("./components/LearnEarn").then((module) => ({
    default: module.default || module.LearnEarn,
  }))
);

const AITradingAssistant = lazy(() => import("./components/AITradingAssistant"));
const AICopyTrading = lazy(() => import("./components/AICopyTrading"));
const AIPortfolioManager = lazy(() => import("./components/AIPortfolioManager"));
const SocialTrading = lazy(() => import("./components/SocialTrading"));
const AIRiskManager = lazy(() => import("./components/AIRiskManager"));
const AIProfitCalculator = lazy(() => import("./components/AIProfitCalculator"));
const AIMarketScanner = lazy(() => import("./components/AIMarketScanner"));
const AINews = lazy(() => import("./components/AINews"));
const AIWhaleTracker = lazy(() => import("./components/AIWhaleTracker"));
const AIArbitrageScanner = lazy(() => import("./components/AIArbitrageScanner"));
const AIGridTrading = lazy(() => import("./components/AIGridTrading"));
const AISmartAlerts = lazy(() => import("./components/AISmartAlerts"));
const AILaunchpad = lazy(() => import("./components/AILaunchpad"));
const AIWhaleHeatmap = lazy(() => import("./components/AIWhaleHeatmap"));
const AITrustScore = lazy(() => import("./components/AITrustScore"));
const AIWhaleAlert = lazy(() => import("./components/AIWhaleAlert"));
const ExaltUtilityCenter = lazy(() => import("./components/ExaltUtilityCenter"));
const ReputationCenter = lazy(() => import("./components/ReputationCenter"));
const AchievementCenter = lazy(() => import("./components/AchievementCenter"));
const NotificationCenter = lazy(() => import("./components/NotificationCenter"));
const LegalHome = lazy(() =>
  import("./pages/legal/LegalHome.jsx").then((module) => ({
    default: module.default || module.LegalHome,
  }))
);

const PrivacyPolicy = lazy(() =>
  import("./pages/legal/PrivacyPolicy.jsx").then((module) => ({
    default: module.default || module.PrivacyPolicy,
  }))
);

const TermsOfService = lazy(() =>
  import("./pages/legal/TermsOfService.jsx").then((module) => ({
    default: module.default || module.TermsOfService,
  }))
);

const AMLPolicy = lazy(() =>
  import("./pages/legal/AMLPolicy.jsx").then((module) => ({
    default: module.default || module.AMLPolicy,
  }))
);

const KYCPolicy = lazy(() =>
  import("./pages/legal/KYCPolicy.jsx").then((module) => ({
    default: module.default || module.KYCPolicy,
  }))
);

const RiskDisclosure = lazy(() =>
  import("./pages/legal/RiskDisclosure.jsx").then((module) => ({
    default: module.default || module.RiskDisclosure,
  }))
);

const CookiePolicy = lazy(() =>
  import("./pages/legal/CookiePolicy.jsx").then((module) => ({
    default: module.default || module.CookiePolicy,
  }))
);

const RefundPolicy = lazy(() =>
  import("./pages/legal/RefundPolicy.jsx").then((module) => ({
    default: module.default || module.RefundPolicy,
  }))
);

const Compliance = lazy(() =>
  import("./pages/legal/Compliance.jsx").then((module) => ({
    default: module.default || module.Compliance,
  }))
);

const DeleteAccount = lazy(() =>
  import("./pages/legal/DeleteAccount.jsx").then((module) => ({
    default: module.default || module.DeleteAccount,
  }))
);
const BlogHome = lazy(() =>
  import("./pages/blog/BlogHome.jsx").then((module) => ({
    default: module.default || module.BlogHome,
  }))
);

const BlogArticle = lazy(() =>
  import("./pages/blog/BlogArticle.jsx").then((module) => ({
    default: module.default || module.BlogArticle,
  }))
);
const AdminPanel = lazy(() =>
  import("./AdminPanel").then((module) => ({
    default: module.default || module.AdminPanel,
  }))
);

const AdminKycPanel = lazy(() =>
  import("./components/AdminKycPanel").then((module) => ({
    default: module.default || module.AdminKycPanel,
  }))
);

const AdminP2P = lazy(() =>
  import("./components/AdminP2P").then((module) => ({
    default: module.default || module.AdminP2P,
  }))
);

const AdminLearnEarn = lazy(() =>
  import("./components/AdminLearnEarn").then((module) => ({
    default: module.default || module.AdminLearnEarn,
  }))
);

const AdminRewards = lazy(() =>
  import("./components/AdminRewards").then((module) => ({
    default: module.default || module.AdminRewards,
  }))
);

const AdminReferrals = lazy(() =>
  import("./components/AdminReferrals").then((module) => ({
    default: module.default || module.AdminReferrals,
  }))
);

const Certificates = lazy(() =>
  import("./components/Certificates").then((module) => ({
    default: module.default || module.Certificates,
  }))
);

const OwnerControl = lazy(() =>
  import("./components/OwnerControl").then((module) => ({
    default: module.default || module.OwnerControl,
  }))
);

const SuperAdminPanel = lazy(() =>
  import("./components/SuperAdminPanel").then((module) => ({
    default: module.default || module.SuperAdminPanel,
  }))
);

const ModeratorPanel = lazy(() =>
  import("./components/ModeratorPanel").then((module) => ({
    default: module.default || module.ModeratorPanel,
  }))
);
import SEO from "./components/SEO";
import WebPageSchema from "./components/SEO/WebPageSchema";
import BreadcrumbSchema from "./components/SEO/BreadcrumbSchema";
import FAQSchema from "./components/SEO/FAQSchema";
import { getBlogPostBySlug } from "./pages/blog/blogData";
const DEFAULT_API_BASE =
  "https://api.exaltexchange.io";

const normalizeApiBase = (value) => {
  const base = String(value || DEFAULT_API_BASE)
    .trim()
    .replace(/\/+$/, "");

  return base.endsWith("/api") ? base.slice(0, -4) : base;
};

const readStoredUser = () => {
  try {
    const storedUser = localStorage.getItem("user");

    if (!storedUser) {
      return {};
    }

    const parsedUser = JSON.parse(storedUser);

    return parsedUser && typeof parsedUser === "object"
      ? parsedUser
      : {};
  } catch (error) {
    console.error("Invalid stored user data:", error);
    localStorage.removeItem("user");
    return {};
  }
};

const checkAdminAccess = (user) =>
  user?.role === "admin" ||
  user?.role === "super_admin" ||
  user?.role === "owner" ||
  user?.isAdmin === true ||
  user?.isOwner === true;
const PUBLIC_ROUTE_SEO = {
  "/": {
    title:
      "Exalt Exchange | Secure Crypto Trading, Web3 Wallet & P2P",
    description:
      "Trade digital assets securely with Exalt Exchange through spot trading, futures, P2P services, Web3 wallet access, staking, KYC, and the EXALT ecosystem.",
    imageAlt:
      "Exalt Exchange secure cryptocurrency trading platform",
  },

  "/legal": {
    title:
      "Legal Center | Policies and Compliance | Exalt Exchange",
    description:
      "Access the official legal policies, compliance standards, user protections, risk disclosures, and regulatory information of Exalt Exchange.",
    imageAlt:
      "Exalt Exchange official Legal Center",
  },

  "/privacy": {
    title:
      "Privacy Policy | Data Protection | Exalt Exchange",
    description:
      "Learn how Exalt Exchange collects, uses, protects, stores, and manages personal information and account data.",
    imageAlt:
      "Exalt Exchange Privacy Policy and data protection",
  },

  "/terms": {
    title:
      "Terms of Service | User Agreement | Exalt Exchange",
    description:
      "Read the official Exalt Exchange Terms of Service covering platform access, user responsibilities, trading services, and account usage.",
    imageAlt:
      "Exalt Exchange Terms of Service",
  },

  "/aml": {
    title:
      "AML Policy | Anti-Money Laundering | Exalt Exchange",
    description:
      "Review the Exalt Exchange Anti-Money Laundering policy, transaction monitoring standards, compliance procedures, and prohibited activities.",
    imageAlt:
      "Exalt Exchange Anti-Money Laundering Policy",
  },

  "/kyc-policy": {
    title:
      "KYC Policy | Identity Verification | Exalt Exchange",
    description:
      "Understand the Exalt Exchange Know Your Customer policy, identity verification requirements, account checks, and compliance procedures.",
    imageAlt:
      "Exalt Exchange KYC identity verification policy",
  },

  "/risk": {
    title:
      "Crypto Risk Disclosure | Exalt Exchange",
    description:
      "Review important risks related to cryptocurrency trading, price volatility, futures, liquidity, digital wallets, and digital assets.",
    imageAlt:
      "Exalt Exchange cryptocurrency risk disclosure",
  },

  "/cookies": {
    title:
      "Cookie Policy | Website Data Usage | Exalt Exchange",
    description:
      "Learn how Exalt Exchange uses cookies and similar technologies to provide security, functionality, analytics, and user preferences.",
    imageAlt:
      "Exalt Exchange Cookie Policy",
  },

  "/refund": {
    title:
      "Refund and Cancellation Policy | Exalt Exchange",
    description:
      "Read the official Exalt Exchange refund and cancellation policy for applicable services, payments, fees, and digital asset transactions.",
    imageAlt:
      "Exalt Exchange Refund and Cancellation Policy",
  },

  "/compliance": {
    title:
      "Compliance Statement | Exalt Exchange",
    description:
      "Explore the compliance principles, security standards, user verification practices, and operational controls followed by Exalt Exchange.",
    imageAlt:
      "Exalt Exchange compliance standards",
  },

  "/delete-account": {
    title:
      "Delete Your Account | Exalt Exchange",
    description:
      "Review the process for requesting permanent deletion of your Exalt Exchange account and associated personal information.",
    imageAlt:
      "Exalt Exchange account deletion information",
  },
};
function App() {
  const { t } = useI18n();
  const { theme, toggleTheme } = useTheme();

  const path =
    window.location.pathname.replace(/\/+$/, "") || "/";
const publicSeo =
  PUBLIC_ROUTE_SEO[path] ||
  PUBLIC_ROUTE_SEO["/"];
  const API_BASE = useMemo(
    () => normalizeApiBase(import.meta.env.VITE_API_URL),
    []
  );

  const [page, setPage] = useState(() =>
    localStorage.getItem("token") ? "dashboard" : "auth"
  );

  const [wallet, setWallet] = useState("");
  const [bnbBalance, setBnbBalance] = useState("0.0000");
  const [menuOpen, setMenuOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(readStoredUser);
  const [authChecking, setAuthChecking] = useState(
    Boolean(localStorage.getItem("token"))
  );

  const token = localStorage.getItem("token");
  const isLoggedIn = Boolean(token);
  const hasAdminAccess = checkAdminAccess(currentUser);

  const currentRole = String(currentUser?.role || "")
    .trim()
    .toLowerCase();

  const hasOwnerAccess =
    currentRole === "owner" &&
    currentUser?.isOwner === true;

  const hasSuperAdminAccess =
    hasOwnerAccess ||
    currentRole === "super_admin";

  const hasModeratorAccess =
    hasSuperAdminAccess ||
    currentRole === "admin" ||
    currentRole === "moderator" ||
    currentUser?.isAdmin === true;

  const translateWithFallback = (
    key,
    fallback,
    namespace
  ) => {
    try {
      const translatedValue = t(key, {
        defaultValue: fallback,
        ...(namespace ? { ns: namespace } : {}),
      });

      if (
        translatedValue === undefined ||
        translatedValue === null ||
        String(translatedValue).trim() === "" ||
        translatedValue === key
      ) {
        return fallback;
      }

      return translatedValue;
    } catch (error) {
      console.error(
        `Translation failed for key "${key}":`,
        error
      );

      return fallback;
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = localStorage.getItem("token");

      if (!storedToken) {
        setCurrentUser({});
        setPage("auth");
        setAuthChecking(false);
        return;
      }

      try {
        const response = await fetch(
          `${API_BASE}/api/auth/me`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${storedToken}`,
              Accept: "application/json",
            },
          }
        );

        const data = await response.json().catch(() => ({}));

        if (response.ok && data?.success && data?.user) {
          localStorage.setItem(
            "user",
            JSON.stringify(data.user)
          );

          setCurrentUser(data.user);

          setPage((currentPage) =>
            currentPage === "auth"
              ? "dashboard"
              : currentPage
          );
        } else {
          localStorage.removeItem("token");
          localStorage.removeItem("user");

          setCurrentUser({});
          setPage("auth");
        }
      } catch (error) {
        console.error(
          "Authentication check failed:",
          error
        );

        localStorage.removeItem("token");
        localStorage.removeItem("user");

        setCurrentUser({});
        setPage("auth");
      } finally {
        setAuthChecking(false);
      }
    };

    checkAuth();
  }, [API_BASE]);

if (path === "/certificates" || path === "/certificates/verify") {
  return (
    <Suspense fallback={<div className="panel"><h2>Loading certificates...</h2></div>}>
      <Certificates mode={path.endsWith("/verify") ? "verify" : "public"} />
    </Suspense>
  );
}

if (path === "/legal") {
  return (
    <>
      <SEO
        title={publicSeo.title}
        description={publicSeo.description}
        path={path}
        imageAlt={publicSeo.imageAlt}
      />

      <WebPageSchema
        title={publicSeo.title}
        description={publicSeo.description}
        path={path}
      />

      <BreadcrumbSchema
        items={[
          {
            name: "Home",
            path: "/",
          },
          {
            name: "Legal Center",
            path: "/legal",
          },
        ]}
      />

      <Suspense
        fallback={
          <div className="panel">
            <h2>Loading Legal Page...</h2>
            <p>Please wait while the page loads.</p>
          </div>
        }
      >
        <LegalHome />
      </Suspense>
    </>
  );
}
if (path === "/privacy") {
  return (
    <>
      <SEO
        title={publicSeo.title}
        description={publicSeo.description}
        path={path}
        imageAlt={publicSeo.imageAlt}
      />

      <WebPageSchema
        title={publicSeo.title}
        description={publicSeo.description}
        path={path}
      />

      <BreadcrumbSchema
        items={[
          {
            name: "Home",
            path: "/",
          },
          {
            name: "Legal Center",
            path: "/legal",
          },
          {
            name: "Privacy Policy",
            path: "/privacy",
          },
        ]}
      />

      <Suspense
        fallback={
          <div className="panel">
            Loading...
          </div>
        }
      >
        <PrivacyPolicy />
      </Suspense>
    </>
  );
}
if (path === "/terms") {
  return (
    <>
      <SEO
        title={publicSeo.title}
        description={publicSeo.description}
        path={path}
        imageAlt={publicSeo.imageAlt}
      />

      <WebPageSchema
        title={publicSeo.title}
        description={publicSeo.description}
        path={path}
      />

      <BreadcrumbSchema
        items={[
          {
            name: "Home",
            path: "/",
          },
          {
            name: "Legal Center",
            path: "/legal",
          },
          {
            name: "Terms of Service",
            path: "/terms",
          },
        ]}
      />

      <Suspense fallback={<div className="panel">Loading...</div>}>
        <TermsOfService />
      </Suspense>
    </>
  );
}
if (path === "/aml") {
  return (
    <>
      <SEO
        title={publicSeo.title}
        description={publicSeo.description}
        path={path}
        imageAlt={publicSeo.imageAlt}
      />

      <WebPageSchema
        title={publicSeo.title}
        description={publicSeo.description}
        path={path}
      />

      <BreadcrumbSchema
        items={[
          {
            name: "Home",
            path: "/",
          },
          {
            name: "Legal Center",
            path: "/legal",
          },
          {
            name: "AML Policy",
            path: "/aml",
          },
        ]}
      />

      <Suspense fallback={<div className="panel">Loading...</div>}>
        <AMLPolicy />
      </Suspense>
    </>
  );
}
 
 if (path === "/kyc-policy") {
  return (
    <>
      <SEO
        title={publicSeo.title}
        description={publicSeo.description}
        path={path}
        imageAlt={publicSeo.imageAlt}
      />

      <WebPageSchema
        title={publicSeo.title}
        description={publicSeo.description}
        path={path}
      />

      <BreadcrumbSchema
        items={[
          {
            name: "Home",
            path: "/",
          },
          {
            name: "Legal Center",
            path: "/legal",
          },
          {
            name: "KYC Policy",
            path: "/kyc-policy",
          },
        ]}
      />

      <Suspense
        fallback={
          <div className="panel">
            Loading...
          </div>
        }
      >
        <KYCPolicy />
      </Suspense>
    </>
  );
}
if (path === "/risk") {
  return (
    <>
      <SEO
        title={publicSeo.title}
        description={publicSeo.description}
        path={path}
        imageAlt={publicSeo.imageAlt}
      />

      <WebPageSchema
        title={publicSeo.title}
        description={publicSeo.description}
        path={path}
      />

      <BreadcrumbSchema
        items={[
          {
            name: "Home",
            path: "/",
          },
          {
            name: "Legal Center",
            path: "/legal",
          },
          {
            name: "Risk Disclosure",
            path: "/risk",
          },
        ]}
      />

      <Suspense
        fallback={
          <div className="panel">
            Loading...
          </div>
        }
      >
        <RiskDisclosure />
      </Suspense>
    </>
  );
}

if (path === "/cookies") {
  return (
    <>
      <SEO
        title={publicSeo.title}
        description={publicSeo.description}
        path={path}
        imageAlt={publicSeo.imageAlt}
      />

      <WebPageSchema
        title={publicSeo.title}
        description={publicSeo.description}
        path={path}
      />

      <BreadcrumbSchema
        items={[
          {
            name: "Home",
            path: "/",
          },
          {
            name: "Legal Center",
            path: "/legal",
          },
          {
            name: "Cookie Policy",
            path: "/cookies",
          },
        ]}
      />

      <Suspense
        fallback={
          <div className="panel">
            Loading...
          </div>
        }
      >
        <CookiePolicy />
      </Suspense>
    </>
  );
}

if (path === "/refund") {
  return (
    <>
      <SEO
        title={publicSeo.title}
        description={publicSeo.description}
        path={path}
        imageAlt={publicSeo.imageAlt}
      />

      <WebPageSchema
        title={publicSeo.title}
        description={publicSeo.description}
        path={path}
      />

      <BreadcrumbSchema
        items={[
          {
            name: "Home",
            path: "/",
          },
          {
            name: "Legal Center",
            path: "/legal",
          },
          {
            name: "Refund Policy",
            path: "/refund",
          },
        ]}
      />

      <Suspense
        fallback={
          <div className="panel">
            Loading...
          </div>
        }
      >
        <RefundPolicy />
      </Suspense>
    </>
  );
}
 
if (path === "/compliance") {
  return (
    <>
      <SEO
        title={publicSeo.title}
        description={publicSeo.description}
        path={path}
        imageAlt={publicSeo.imageAlt}
      />

      <WebPageSchema
        title={publicSeo.title}
        description={publicSeo.description}
        path={path}
      />

      <BreadcrumbSchema
        items={[
          {
            name: "Home",
            path: "/",
          },
          {
            name: "Legal Center",
            path: "/legal",
          },
          {
            name: "Compliance",
            path: "/compliance",
          },
        ]}
      />

      <Suspense
        fallback={
          <div className="panel">
            Loading...
          </div>
        }
      >
        <Compliance />
      </Suspense>
    </>
  );
}

 if (path === "/delete-account") {
  return (
    <>
      <SEO
        title={publicSeo.title}
        description={publicSeo.description}
        path={path}
        imageAlt={publicSeo.imageAlt}
      />

      <WebPageSchema
        title={publicSeo.title}
        description={publicSeo.description}
        path={path}
      />

      <BreadcrumbSchema
        items={[
          {
            name: "Home",
            path: "/",
          },
          {
            name: "Legal Center",
            path: "/legal",
          },
          {
            name: "Delete Account",
            path: "/delete-account",
          },
        ]}
      />

      <Suspense
        fallback={
          <div className="panel">
            Loading...
          </div>
        }
      >
        <DeleteAccount />
      </Suspense>
    </>
  );
}
if (path === "/blog") {
  return (
    <>
      <SEO
        title={publicSeo.title}
        description={publicSeo.description}
        path={path}
        imageAlt={publicSeo.imageAlt}
      />

      <WebPageSchema
        title={publicSeo.title}
        description={publicSeo.description}
        path={path}
      />

      <BreadcrumbSchema
        items={[
          {
            name: "Home",
            path: "/",
          },
          {
            name: "Blog",
            path: "/blog",
          },
        ]}
      />

      <Suspense
        fallback={
          <div className="panel">
            <h2>Loading Blog...</h2>
            <p>Please wait while articles load.</p>
          </div>
        }
      >
        <BlogHome
          onOpenArticle={(slug) => {
            window.location.href = `/blog/${slug}`;
          }}
        />
      </Suspense>
    </>
  );
}
if (path.startsWith("/blog/")) {
  const slug = decodeURIComponent(
    path.replace("/blog/", "")
  );

  const post = getBlogPostBySlug(slug);

  if (!post) {
    window.location.replace("/blog");
    return null;
  }

  return (
    <>
      <SEO
        title={post.seoTitle}
        description={post.seoDescription}
        path={path}
        imageAlt={post.imageAlt}
      />

      <WebPageSchema
        title={post.seoTitle}
        description={post.seoDescription}
        path={path}
      />

      <BreadcrumbSchema
        items={[
          {
            name: "Home",
            path: "/",
          },
          {
            name: "Blog",
            path: "/blog",
          },
          {
            name: post.title,
            path: path,
          },
        ]}
      />

      <Suspense
        fallback={
          <div className="panel">
            <h2>Loading Article...</h2>
            <p>Please wait while the article loads.</p>
          </div>
        }
      >
        <BlogArticle
          slug={slug}
          onBack={() => {
            window.location.href = "/blog";
          }}
        />
      </Suspense>
    </>
  );
}
  if (path.startsWith("/verify-email/")) {
    return <VerifyEmail />;
  }

  if (path.startsWith("/reset-password/")) {
    return <ResetPassword />;
  }

  if (path.startsWith("/ref/")) {
    const referralCode = path.split("/ref/")[1];

    if (referralCode) {
      localStorage.setItem(
        "pendingReferralCode",
        decodeURIComponent(referralCode)
      );

      window.history.replaceState({}, "", "/");
    }
  }

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");

    setWallet("");
    setBnbBalance("0.0000");
    setCurrentUser({});
    setMenuOpen(false);
    setPage("auth");

    window.history.replaceState({}, "", "/");
  };

  const connectWallet = async () => {
    try {
      const isMobile =
        /Android|iPhone|iPad|iPod/i.test(
          navigator.userAgent
        );

      const siteUrl = "exaltexchange.io";

      if (!window.ethereum) {
        if (isMobile) {
          window.location.href =
            `https://metamask.app.link/dapp/${siteUrl}`;
          return;
        }

        window.alert("Please install MetaMask extension");
        return;
      }

      try {
        await window.ethereum.request({
          method: "wallet_switchEthereumChain",
          params: [{ chainId: "0x38" }],
        });
      } catch (switchError) {
        if (switchError?.code === 4902) {
          await window.ethereum.request({
            method: "wallet_addEthereumChain",
            params: [
              {
                chainId: "0x38",
                chainName: "BNB Smart Chain",
                nativeCurrency: {
                  name: "BNB",
                  symbol: "BNB",
                  decimals: 18,
                },
                rpcUrls: [
                  "https://bsc-dataseed.binance.org/",
                ],
                blockExplorerUrls: [
                  "https://bscscan.com",
                ],
              },
            ],
          });
        } else {
          throw switchError;
        }
      }

      const { BrowserProvider, formatEther } =
  await import("ethers");

const provider = new BrowserProvider(
  window.ethereum
);

const accounts = await provider.send(
  "eth_requestAccounts",
  []
);

      if (!accounts?.length) {
        window.alert("No wallet account found");
        return;
      }

      const address = accounts[0];
      const balance = await provider.getBalance(address);

      setWallet(address);
    setBnbBalance(
  Number(formatEther(balance)).toFixed(4)
);
      window.alert("Wallet Connected Successfully");
    } catch (error) {
      console.error(
        "Wallet connection failed:",
        error
      );

      window.alert("Wallet connection failed");
    }
  };

  const shortWallet = wallet
    ? `${wallet.slice(0, 6)}...${wallet.slice(-4)}`
    : translateWithFallback(
        "connectWallet",
        "Connect Wallet",
        "web3"
      );

  const adminOnlyPanel = (
    Component,
    componentProps = {}
  ) => {
    if (!hasAdminAccess) {
      return (
        <div className="panel">
          <h2>
            {translateWithFallback(
              "accessDenied",
              "Access Denied",
              "common"
            )}
          </h2>

          <p>
            Only an authorized Owner, Super Admin or Admin
            can access this panel.
          </p>

          <button
            type="button"
            className="buy-btn"
            onClick={() => setPage("dashboard")}
          >
            Return to Dashboard
          </button>
        </div>
      );
    }
return (
  <Suspense
    fallback={
      <div className="panel">
        <h2>Loading Admin Panel...</h2>
        <p>Please wait while the secure panel loads.</p>
      </div>
    }
  >
    <Component {...componentProps} />
  </Suspense>
);
  
  };

  const ownerOnlyPanel = (
    Component,
    componentProps = {}
  ) => {
    if (!hasOwnerAccess) {
      return (
        <div className="panel">
          <h2>
            {translateWithFallback(
              "accessDenied",
              "Access Denied",
              "common"
            )}
          </h2>

          <p>
            Only the verified Exalt Exchange Owner can access
            this control center.
          </p>

          <button
            type="button"
            className="buy-btn"
            onClick={() => setPage("dashboard")}
          >
            Return to Dashboard
          </button>
        </div>
      );
    }

  return (
  <Suspense
    fallback={
      <div className="panel">
        <h2>Loading Owner Control...</h2>
        <p>Please wait while the secure panel loads.</p>
      </div>
    }
  >
    <Component
      setPage={setPage}
      currentUser={currentUser}
      {...componentProps}
    />
  </Suspense>
);
};
  const superAdminOnlyPanel = (
    Component,
    componentProps = {}
  ) => {
    if (!hasSuperAdminAccess) {
      return (
        <div className="panel">
          <h2>
            {translateWithFallback(
              "accessDenied",
              "Access Denied",
              "common"
            )}
          </h2>

          <p>
            Only the Owner or Super Admin can access this
            operations panel.
          </p>

          <button
            type="button"
            className="buy-btn"
            onClick={() => setPage("dashboard")}
          >
            Return to Dashboard
          </button>
        </div>
      );
    }

   return (
  <Suspense
    fallback={
      <div className="panel">
        <h2>Loading Secure Panel...</h2>
        <p>Please wait while the secure panel loads.</p>
      </div>
    }
  >
    <Component
      setPage={setPage}
      currentUser={currentUser}
      {...componentProps}
    />
  </Suspense>
);
  };
  const moderatorOnlyPanel = (
    Component,
    componentProps = {}
  ) => {
    if (!hasModeratorAccess) {
      return (
        <div className="panel">
          <h2>
            {translateWithFallback(
              "accessDenied",
              "Access Denied",
              "common"
            )}
          </h2>

          <p>
            This panel is restricted to authorized management
            and moderator accounts.
          </p>

          <button
            type="button"
            className="buy-btn"
            onClick={() => setPage("dashboard")}
          >
            Return to Dashboard
          </button>
        </div>
      );
    }

    return (
      <Component
        setPage={setPage}
        currentUser={currentUser}
        {...componentProps}
      />
    );
  };

  const renderPage = () => {
    if (page === "dashboard") {
      return (
        <>
          <Dashboard setPage={setPage} />
          <TradingPanel />
          <OrderBook />
        </>
      );
    }

    if (page === "forgot-password") {
      return <ForgotPassword setPage={setPage} />;
    }

    if (page === "verify-reset-code") {
      return <VerifyResetCode setPage={setPage} />;
    }

    if (page === "reset-password") {
      return <ResetPassword setPage={setPage} />;
    }

    if (page === "markets") {
      return <Markets />;
    }

    if (page === "trade") {
      return <Trade setPage={setPage} />;
    }

    if (page === "buy") {
      return <BuyCrypto />;
    }

    if (page === "futures") {
      return <Futures setPage={setPage} />;
    }

    if (page === "wallets") {
      return <Wallets />;
    }

    if (page === "assets") {
      return <Assets setPage={setPage} />;
    }

    if (page === "tradfi") {
      return <TradFi />;
    }

    if (page === "services") {
      return <ServicesHub setPage={setPage} />;
    }

    if (page === "exalt-card") {
      return <ExaltCard />;
    }

    if (page === "web3wallet") {
      return <Web3Wallet setPage={setPage} />;
    }

    if (page === "transactions") {
      return <Transactions />;
    }

    if (page === "certificates") {
      return <Certificates mode="my" currentUser={currentUser} setPage={setPage} />;
    }

    if (page === "orders") {
      return <Orders />;
    }

    if (page === "p2p") {
      return <P2P />;
    }

    if (page === "kyc") {
      return adminOnlyPanel(AdminKycPanel);
    }

    if (page === "kyc-submit") {
      return <KycVerification />;
    }

    if (page === "referral") {
      return <Referral />;
    }

    if (page === "rewards") {
      return <ReplitRewards />;
    }

    if (page === "support") {
      return <Support />;
    }

    if (page === "listings") {
      return <ListingForm />;
    }

    if (page === "profile") {
      return <Profile setPage={setPage} />;
    }

    if (page === "staking") {
      return <Staking />;
    }

    if (page === "learnearn") {
      return <LearnEarn />;
    }

    if (page === "ai-assistant") {
      return <AITradingAssistant />;
    }

    if (page === "ai-copy-trading") {
      return <AICopyTrading />;
    }

    if (page === "ai-portfolio") {
      return <AIPortfolioManager />;
    }

    if (page === "social-trading") {
      return <SocialTrading />;
    }

    if (page === "ai-risk-manager") {
      return <AIRiskManager />;
    }

    if (page === "ai-profit-calculator") {
      return <AIProfitCalculator />;
    }

    if (page === "ai-market-scanner") {
      return <AIMarketScanner />;
    }

    if (page === "ai-news") {
      return <AINews />;
    }

    if (page === "ai-whale-tracker") {
      return <AIWhaleTracker />;
    }

    if (page === "ai-arbitrage-scanner") {
      return <AIArbitrageScanner />;
    }

    if (page === "ai-grid-trading") {
      return <AIGridTrading />;
    }

    if (page === "ai-smart-alerts") {
      return <AISmartAlerts />;
    }

    if (page === "ai-launchpad") {
      return <AILaunchpad />;
    }

    if (page === "ai-whale-heatmap") {
      return <AIWhaleHeatmap />;
    }

    if (page === "ai-trust-score") {
      return <AITrustScore />;
    }

    if (page === "ai-whale-alerts") {
      return <AIWhaleAlert />;
    }

    if (page === "exalt-utility-center") {
      return <ExaltUtilityCenter />;
    }

    if (page === "reputation-center") {
      return <ReputationCenter />;
    }

    if (page === "achievement-center") {
      return <AchievementCenter />;
    }

    if (page === "notification-center") {
      return <NotificationCenter setPage={setPage} />;
    }

    if (page === "admin-p2p") {
      return adminOnlyPanel(AdminP2P);
    }

    if (page === "admin") {
      return adminOnlyPanel(AdminPanel);
    }

    if (page === "admin-rewards") {
      return adminOnlyPanel(AdminRewards);
    }

    if (page === "admin-learn") {
      return adminOnlyPanel(AdminLearnEarn);
    }

    if (page === "admin-referrals") {
      return adminOnlyPanel(AdminReferrals);
    }

    if (page === "owner-control") {
      return ownerOnlyPanel(OwnerControl);
    }

    if (page === "super-admin") {
      return superAdminOnlyPanel(SuperAdminPanel);
    }

    if (page === "moderator-panel") {
      return moderatorOnlyPanel(ModeratorPanel);
    }

    if (page === "settings") {
      return <Settings setPage={setPage} />;
    }

    return (
      <div className="panel">
        <h2>Page unavailable</h2>

        <p>
          This navigation target is not registered in the current exchange
          interface.
        </p>

        <button
          type="button"
          className="buy-btn"
          onClick={() => setPage("dashboard")}
        >
          Return to Dashboard
        </button>
      </div>
    );
  };

  const menuItems = [
    ["profile", "👤 Profile"],
    ["dashboard", "📊 Dashboard"],
    ["markets", "📈 Markets"],
    ["trade", "💱 Spot Trading"],
    ["futures", "📉 Futures"],
    ["tradfi", "📊 TradFi"],
    ["services", "🧰 Services"],
    ["exalt-card", "💳 EXALT Card"],
    ["buy", "💳 Buy Crypto"],
    ["p2p", "🌍 P2P"],
    ["staking", "🔒 Staking"],
    ["learnearn", "🎓 Learn & Earn"],
    ["blog", "📰 Blog"],
    ["ai-assistant", "🤖 AI Trading Assistant"],
    ["ai-copy-trading", "🔁 AI Copy Trading"],
    ["ai-portfolio", "📂 AI Portfolio Manager"],
    ["social-trading", "👥 Social Trading"],
    ["ai-risk-manager", "🛡️ AI Risk Manager"],
    [
      "ai-profit-calculator",
      "💰 AI Profit Calculator",
    ],
    ["ai-market-scanner", "🔎 AI Market Scanner"],
    ["ai-news", "📰 AI News"],
    ["ai-whale-tracker", "🐋 AI Whale Tracker"],
    [
      "ai-arbitrage-scanner",
      "⚡ AI Arbitrage Scanner",
    ],
    ["ai-grid-trading", "🧮 AI Grid Trading"],
    ["ai-smart-alerts", "🚨 AI Smart Alerts"],
    ["ai-launchpad", "🚀 AI Launchpad"],
    ["ai-whale-heatmap", "🔥 AI Whale Heatmap"],
    ["ai-trust-score", "✅ AI Trust Score"],
    ["ai-whale-alerts", "🐳 AI Whale Alerts"],
    [
      "exalt-utility-center",
      "🧰 Exalt Utility Center",
    ],
    [
      "reputation-center",
      "⭐ Community Reputation",
    ],
    [
      "achievement-center",
      "🏆 Achievement Center",
    ],
    [
      "notification-center",
      "🔔 Notification Center",
    ],
    ["wallets", "👛 Wallets"],
    ["assets", "💰 Assets"],
    ["web3wallet", "🌐 Web3 Wallet"],
    ["orders", "📦 Orders"],
    ["kyc-submit", "📝 Submit KYC"],
    ["listings", "📌 Submit Listing"],
    ["referral", "🤝 Referral"],
    ["transactions", "📜 Transactions"],
    ["certificates", "🏅 My Certificates"],
    ["rewards", "🎁 Rewards"],
    ["support", "🎧 Support"],
    ["settings", "⚙️ Settings"],
  ];

  const adminMenuItems = [
    ["certificates", "🏅 Certificates"],
    ["admin-p2p", "🧾 Admin P2P"],
    ["kyc", "🪪 KYC Requests"],
    ["admin-learn", "🎓 Admin Learn & Earn"],
    ["admin-referrals", "🤝 Admin Referrals"],
    ["admin-rewards", "🎁 Admin Rewards"],
    ["admin", "⚙️ Admin Panel"],
  ];

  const ownerMenuItems = [
    ["owner-control", "👑 Owner Control"],
  ];

  const superAdminMenuItems = [
    ["super-admin", "🛡️ Super Admin"],
  ];

  const moderatorMenuItems = [
    ["moderator-panel", "🧰 Moderator Panel"],
  ];
const openPage = (pageName) => {
  if (pageName === "blog") {
    window.location.href = "/blog";
    return;
  }

  setPage(pageName);
  setMenuOpen(false);
};

/*
  App Shell migration: navigation-building logic (icon/label
  parsing, access gating, shape conversion for AppShell.jsx) lives
  in navigation/buildShellNavItems.js so it's directly unit
  testable - see tests/appShellIntegration.test.mjs. The
  menuItems/adminMenuItems/etc. tuples above remain the one and
  only navigation data source; nothing is duplicated.
*/
const shellNavItems = buildShellNavItems({
  menuItems,
  adminMenuItems,
  ownerMenuItems,
  superAdminMenuItems,
  moderatorMenuItems,
  access: {
    admin: hasAdminAccess,
    owner: hasOwnerAccess,
    superAdmin: hasSuperAdminAccess,
    moderator: hasModeratorAccess,
  },
  translate: translateWithFallback,
  onSelect: openPage,
});

if (authChecking && isLoggedIn) {
  return (
    <>
      <SEO
        title="Secure Session | Exalt Exchange"
        description="Secure Exalt Exchange account verification."
        path="/"
        noIndex
      />

      <div className="app">
        <main className="main auth-only">
          <div className="panel">
            <h2>Verifying secure session...</h2>

            <p>
              Please wait while your account is verified.
            </p>
          </div>
        </main>
      </div>
    </>
  );
}
 if (!isLoggedIn) {
  const authTitle =
    page === "forgot-password"
      ? "Forgot Password | Exalt Exchange"
      : page === "verify-reset-code"
        ? "Verify Reset Code | Exalt Exchange"
        : page === "reset-password"
          ? "Reset Password | Exalt Exchange"
          : "Login | Exalt Exchange";

  return (
<>
   <SEO
  title={authTitle}
  description="Secure account access and password recovery for Exalt Exchange users."
  path="/"
/>

      <div className="app">
        <main className="main auth-only">
          {page === "forgot-password" && (
            <ForgotPassword setPage={setPage} />
          )}

          {page === "verify-reset-code" && (
            <VerifyResetCode setPage={setPage} />
          )}

          {page === "reset-password" && (
            <ResetPassword setPage={setPage} />
          )}

          {![
            "forgot-password",
            "verify-reset-code",
            "reset-password",
          ].includes(page) && (
        
            <AuthPanel
              setPage={setPage}
              setCurrentUser={setCurrentUser}
            />
          )}
        </main>
      </div>
    </>
  );
}
return (
  <>
    <SEO
      title="Exalt Exchange Dashboard"
      description="Secure cryptocurrency trading dashboard for authenticated Exalt Exchange users."
      path="/"
      noIndex
    />

    <div
      className={`app page-${page} ${
        page === "dashboard"
          ? "dashboard-app-shell"
          : "inner-page-shell"
      }`}
    >
      <AppShell
        navItems={shellNavItems}
        activeKey={page}
        mobileMenuOpen={menuOpen}
        onMobileMenuOpenChange={setMenuOpen}
        logo={
          <img
            src={exchangeLogo}
            alt="Exalt Exchange"
            className="main-logo"
          />
        }
        notificationSlot={<NotificationBell setPage={setPage} />}
        accountSlot={
          <Button variant="ghost" size="sm" onClick={logout}>
            {translateWithFallback("logout", "Logout", "auth")}
          </Button>
        }
        languageSlot={<LanguageSwitcher compact />}
        themeSlot={
          <Button
            variant="ghost"
            size="sm"
            aria-label={
              theme === "dark"
                ? "Switch to light mode"
                : "Switch to dark mode"
            }
            onClick={toggleTheme}
          >
            {theme === "dark" ? "☀️" : "🌙"}
          </Button>
        }
        sidebarFooter={
          wallet ? (
            <div className="coin-box">
              <p>{shortWallet}</p>
              <p>{bnbBalance} BNB</p>
            </div>
          ) : null
        }
      >
        <Suspense
          fallback={
            <div className="panel">
              <h2>Loading Exalt Exchange...</h2>
              <p>Please wait while this section loads.</p>
            </div>
          }
        >
          {renderPage()}
        </Suspense>

        <footer className="legal-footer-links">
          <a href="/legal">Legal Center</a>
          <a href="/privacy">
            Privacy Policy
          </a>
          <a href="/terms">Terms</a>
          <a href="/delete-account">
            Delete Account
          </a>
          <a href="/aml">AML</a>
          <a href="/kyc-policy">KYC</a>
          <a href="/risk">Risk</a>
          <a href="/cookies">Cookies</a>
          <a href="/refund">Refund</a>
          <a href="/compliance">
            Compliance
          </a>
        </footer>
      </AppShell>
    </div>
  </>
  );
}

export default App;
